import React, { useState, useCallback } from 'react';
import {
  View,
  Text,
  StyleSheet,
  SafeAreaView,
  TouchableOpacity,
  Image,
  ScrollView,
  Alert,
  Switch,
  ActivityIndicator,
  Modal,
  FlatList,
} from 'react-native';
import { ms, s, vs } from '../../utils/responsive';
import { NavigationProp, useFocusEffect } from '@react-navigation/native';
import {
  deathVerificationService,
  DeathAccessPolicy,
  AccessDelay,
  DELAY_LABELS,
  CATEGORY_LABELS,
} from '../../services/deathVerificationService';

// Import icons
import backIcon from '../../assets/icons/back.png';
import calendarIcon from '../../assets/icons/calendar.png';
import lockIcon from '../../assets/icons/lock.png';
import infoIcon from '../../assets/icons/info.png';

import { createLogger } from '../../utils/logger';
const logger = createLogger('DeathAccessPolicy');

interface DeathAccessPolicyScreenProps {
  navigation: NavigationProp<any>;
}

// Map policy fields to categories
const CATEGORY_FIELD_MAP: Record<string, keyof DeathAccessPolicy> = {
  emergency_contacts: 'emergencyContactsDelay',
  funeral_wishes: 'funeralWishesDelay',
  medical_info: 'medicalInfoDelay',
  personal_info: 'personalInfoDelay',
  financial_info: 'financialInfoDelay',
  property_info: 'propertyInfoDelay',
  documents: 'documentsDelay',
  passwords: 'passwordsDelay',
  loyalty_points: 'loyaltyPointsDelay',
  contacts: 'contactsDelay',
  expenses: 'expensesDelay',
};

// Categories that have minimum delay requirements
const SENSITIVE_CATEGORIES = ['passwords', 'loyalty_points'];

// Available delay options
const DELAY_OPTIONS = [
  { value: AccessDelay.IMMEDIATE, label: 'Immediate' },
  { value: AccessDelay.ONE_DAY, label: '1 Day' },
  { value: AccessDelay.SEVEN_DAYS, label: '7 Days' },
  { value: AccessDelay.FOURTEEN_DAYS, label: '14 Days' },
  { value: AccessDelay.THIRTY_DAYS, label: '30 Days' },
  { value: AccessDelay.NEVER, label: 'Never (Manual Only)' },
];

// Filter options for sensitive categories (minimum 24 hours)
const SENSITIVE_DELAY_OPTIONS = DELAY_OPTIONS.filter(
  (opt) => opt.value === AccessDelay.NEVER || opt.value >= AccessDelay.ONE_DAY
);

const DeathAccessPolicyScreen: React.FC<DeathAccessPolicyScreenProps> = ({ navigation }) => {
  const [policy, setPolicy] = useState<DeathAccessPolicy | null>(null);
  const [localPolicy, setLocalPolicy] = useState<Partial<DeathAccessPolicy>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);

  useFocusEffect(
    useCallback(() => {
      loadPolicy();
    }, [])
  );

  const loadPolicy = async () => {
    try {
      setLoading(true);
      logger.debug('[DeathAccessPolicy] Fetching policy...');
      const response = await deathVerificationService.getPolicy();
      logger.debug('[DeathAccessPolicy] Response received:', JSON.stringify(response, null, 2));
      logger.debug('[DeathAccessPolicy] Policy object:', response?.policy);
      setPolicy(response.policy);
      setLocalPolicy({}); // Clear any pending changes
      setHasChanges(false);
    } catch (error: any) {
      logger.error('[DeathAccessPolicy] Error loading policy:', error);
      logger.error('[DeathAccessPolicy] Error message:', error?.message);
      logger.error('[DeathAccessPolicy] Error stack:', error?.stack);
      Alert.alert('Error', 'Failed to load access policy settings');
    } finally {
      setLoading(false);
    }
  };

  // Update local state immediately for UI responsiveness
  const updateCategoryDelay = (category: string, value: number) => {
    const fieldName = CATEGORY_FIELD_MAP[category];
    setLocalPolicy(prev => ({
      ...prev,
      [fieldName]: value,
    }));
    setHasChanges(true);
  };

  // Update boolean setting locally
  const updateBooleanSetting = (field: keyof DeathAccessPolicy, value: boolean) => {
    setLocalPolicy(prev => ({
      ...prev,
      [field]: value,
    }));
    setHasChanges(true);
  };

  // Save all changes to backend
  const saveChanges = async () => {
    if (!hasChanges) {
      Alert.alert('No Changes', 'There are no policy changes to save.');
      return;
    }

    try {
      setSaving(true);
      logger.debug('[DeathAccessPolicy] Current policy before save:', JSON.stringify(policy, null, 2));
      logger.debug('[DeathAccessPolicy] Local changes to save:', JSON.stringify(localPolicy, null, 2));

      // Merge current policy with local changes to send the complete state
      // This ensures all values are explicitly sent, preventing any issues with partial updates
      const mergedPolicy = {
        emergencyContactsDelay: localPolicy.emergencyContactsDelay ?? policy?.emergencyContactsDelay ?? AccessDelay.IMMEDIATE,
        funeralWishesDelay: localPolicy.funeralWishesDelay ?? policy?.funeralWishesDelay ?? AccessDelay.IMMEDIATE,
        medicalInfoDelay: localPolicy.medicalInfoDelay ?? policy?.medicalInfoDelay ?? AccessDelay.ONE_DAY,
        personalInfoDelay: localPolicy.personalInfoDelay ?? policy?.personalInfoDelay ?? AccessDelay.ONE_DAY,
        financialInfoDelay: localPolicy.financialInfoDelay ?? policy?.financialInfoDelay ?? AccessDelay.SEVEN_DAYS,
        propertyInfoDelay: localPolicy.propertyInfoDelay ?? policy?.propertyInfoDelay ?? AccessDelay.SEVEN_DAYS,
        documentsDelay: localPolicy.documentsDelay ?? policy?.documentsDelay ?? AccessDelay.SEVEN_DAYS,
        passwordsDelay: localPolicy.passwordsDelay ?? policy?.passwordsDelay ?? AccessDelay.FOURTEEN_DAYS,
        loyaltyPointsDelay: localPolicy.loyaltyPointsDelay ?? policy?.loyaltyPointsDelay ?? AccessDelay.FOURTEEN_DAYS,
        contactsDelay: localPolicy.contactsDelay ?? policy?.contactsDelay ?? AccessDelay.ONE_DAY,
        expensesDelay: localPolicy.expensesDelay ?? policy?.expensesDelay ?? AccessDelay.SEVEN_DAYS,
        requireSecretCodeValidation: localPolicy.requireSecretCodeValidation ?? policy?.requireSecretCodeValidation ?? true,
        requireExternalVerification: localPolicy.requireExternalVerification ?? policy?.requireExternalVerification ?? true,
        allowLivingConfirmationOverride: localPolicy.allowLivingConfirmationOverride ?? policy?.allowLivingConfirmationOverride ?? true,
      };

      logger.debug('[DeathAccessPolicy] Merged policy to send:', JSON.stringify(mergedPolicy, null, 2));

      const updatedPolicy = await deathVerificationService.updatePolicy(mergedPolicy);

      logger.debug('[DeathAccessPolicy] Response from server:', JSON.stringify(updatedPolicy, null, 2));
      logger.debug('[DeathAccessPolicy] Response emergencyContactsDelay:', updatedPolicy?.emergencyContactsDelay);
      logger.debug('[DeathAccessPolicy] Response requireSecretCodeValidation:', updatedPolicy?.requireSecretCodeValidation);

      if (updatedPolicy && updatedPolicy.id) {
        setPolicy(updatedPolicy);
        setLocalPolicy({});
        setHasChanges(false);
        Alert.alert('Success', 'Settings saved successfully');
      } else {
        logger.error('[DeathAccessPolicy] Invalid response from server:', updatedPolicy);
        Alert.alert('Error', 'Received invalid response from server');
      }
    } catch (error: any) {
      logger.error('[DeathAccessPolicy] Error saving policy:', error);
      Alert.alert('Error', error.message || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  // Get the current value, preferring local changes over saved policy
  const getCategoryValue = (category: string): number => {
    const fieldName = CATEGORY_FIELD_MAP[category];
    // Check local changes first
    if (localPolicy[fieldName] !== undefined) {
      return localPolicy[fieldName] as number;
    }
    // Fall back to saved policy
    if (!policy) return 0;
    return (policy as any)[fieldName] ?? 0;
  };

  // Get boolean value, preferring local changes
  const getBooleanValue = (field: keyof DeathAccessPolicy): boolean => {
    if (localPolicy[field] !== undefined) {
      return localPolicy[field] as boolean;
    }
    if (!policy) return false;
    return (policy as any)[field] ?? false;
  };

  const getDelayLabel = (value: number): string => {
    const option = DELAY_OPTIONS.find(opt => opt.value === value);
    return option?.label || 'Select...';
  };

  const openDropdown = (category: string) => {
    setSelectedCategory(category);
    setModalVisible(true);
  };

  const selectOption = (value: number) => {
    if (selectedCategory) {
      setModalVisible(false);
      updateCategoryDelay(selectedCategory, value);
      setSelectedCategory(null);
    }
  };

  const renderCategoryRow = (category: string) => {
    const isSensitive = SENSITIVE_CATEGORIES.includes(category);
    const currentValue = getCategoryValue(category);
    const currentLabel = getDelayLabel(currentValue);

    return (
      <View key={category} style={styles.categoryRow}>
        <View style={styles.categoryInfo}>
          <Text style={styles.categoryLabel}>{CATEGORY_LABELS[category]}</Text>
          {isSensitive && (
            <View style={styles.sensitiveBadge}>
              <Text style={styles.sensitiveBadgeText}>Sensitive</Text>
            </View>
          )}
        </View>
        <TouchableOpacity
          style={styles.dropdownButton}
          onPress={() => openDropdown(category)}
          disabled={saving}
        >
          <Text style={styles.dropdownButtonText}>{currentLabel}</Text>
          <Image
            source={require('../../assets/icons/chevron.png')}
            style={styles.dropdownChevron}
          />
        </TouchableOpacity>
      </View>
    );
  };

  const renderSwitchRow = (
    label: string,
    description: string,
    field: keyof DeathAccessPolicy,
    value: boolean
  ) => (
    <View style={styles.switchRow}>
      <View style={styles.switchInfo}>
        <Text style={styles.switchLabel}>{label}</Text>
        <Text style={styles.switchDescription}>{description}</Text>
      </View>
      <Switch
        value={value}
        onValueChange={(newValue) => updateBooleanSetting(field, newValue)}
        trackColor={{ false: '#DDD', true: '#29BFC0' }}
        thumbColor="#FFF"
        disabled={saving}
      />
    </View>
  );

  if (loading) {
    return (
      <SafeAreaView style={styles.container}>
        <View style={styles.header}>
          <TouchableOpacity onPress={() => navigation.goBack()}>
            <Image source={backIcon} style={styles.headerIcon} />
          </TouchableOpacity>
          <Text style={styles.headerTitle}>EOL Access Policy</Text>
          <View style={styles.headerPlaceholder} />
        </View>
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color="#29BFC0" />
          <Text style={styles.loadingText}>Loading settings...</Text>
        </View>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={styles.container}>
      <View style={styles.header}>
        <TouchableOpacity onPress={() => navigation.goBack()}>
          <Image source={backIcon} style={styles.headerIcon} />
        </TouchableOpacity>
        <Text style={styles.headerTitle}>EOL Access Policy</Text>
        {saving ? (
          <ActivityIndicator size="small" color="#29BFC0" />
        ) : (
          <View style={styles.headerPlaceholder} />
        )}
      </View>

      <ScrollView
        style={styles.scrollView}
        contentContainerStyle={styles.scrollContent}
        showsVerticalScrollIndicator={false}
      >
        {/* Info Card */}
        <View style={styles.infoCard}>
          <Image source={infoIcon} style={styles.infoCardIcon} />
          <View style={styles.infoCardContent}>
            <Text style={styles.infoCardTitle}>Configure Access Timing</Text>
            <Text style={styles.infoCardDescription}>
              Control when your successor can access different types of your data after an end of life
              event has been verified. Some categories require a minimum delay for security.
            </Text>
          </View>
        </View>

        {/* Timing Section */}
        <View style={styles.section}>
          <View style={styles.sectionHeader}>
            <Image source={calendarIcon} style={styles.sectionIcon} />
            <Text style={styles.sectionTitle}>Access Timing by Category</Text>
          </View>

          <View style={styles.sectionContent}>
            {Object.keys(CATEGORY_FIELD_MAP).map(renderCategoryRow)}
          </View>
        </View>

        {/* Verification Settings */}
        <View style={styles.section}>
          <View style={styles.sectionHeader}>
            <Image source={lockIcon} style={styles.sectionIcon} />
            <Text style={styles.sectionTitle}>Verification Settings</Text>
          </View>

          <View style={styles.sectionContent}>
            {policy && (
              <>
                {renderSwitchRow(
                  'Require Secret Code',
                  'A secret code will be sent to your designated recipients for verification',
                  'requireSecretCodeValidation',
                  getBooleanValue('requireSecretCodeValidation')
                )}

                {renderSwitchRow(
                  'External Verification',
                  'Cross-reference death certificate with official databases when available',
                  'requireExternalVerification',
                  getBooleanValue('requireExternalVerification')
                )}

                {renderSwitchRow(
                  'Living Confirmation',
                  'Attempt to contact you before granting access (fraud prevention)',
                  'allowLivingConfirmationOverride',
                  getBooleanValue('allowLivingConfirmationOverride')
                )}
              </>
            )}
          </View>
        </View>

        {/* Explanation Card */}
        <View style={styles.explanationCard}>
          <Text style={styles.explanationTitle}>How It Works</Text>
          <View style={styles.explanationItem}>
            <Text style={styles.bulletPoint}>1.</Text>
            <Text style={styles.explanationText}>
              When your successor submits a death certificate, AI will analyze it for authenticity.
            </Text>
          </View>
          <View style={styles.explanationItem}>
            <Text style={styles.bulletPoint}>2.</Text>
            <Text style={styles.explanationText}>
              A secret code is sent to your designated recipients who can provide it to your successor.
            </Text>
          </View>
          <View style={styles.explanationItem}>
            <Text style={styles.bulletPoint}>3.</Text>
            <Text style={styles.explanationText}>
              Once verified, your data becomes available according to the timing you set above.
            </Text>
          </View>
          <View style={styles.explanationItem}>
            <Text style={styles.bulletPoint}>4.</Text>
            <Text style={styles.explanationText}>
              Sensitive data like passwords have built-in delays to prevent misuse.
            </Text>
          </View>
        </View>

        {/* Navigate to Recipients */}
        <TouchableOpacity
          style={styles.linkButton}
          onPress={() => navigation.navigate('SecretCodeRecipients')}
        >
          <Text style={styles.linkButtonText}>Manage Secret Code Recipients</Text>
          <Image
            source={require('../../assets/icons/chevron.png')}
            style={styles.linkButtonIcon}
          />
        </TouchableOpacity>

        {/* Save Changes Button */}
        {hasChanges && (
          <TouchableOpacity
            style={[styles.saveButton, saving && styles.saveButtonDisabled]}
            onPress={saveChanges}
            disabled={saving}
          >
            {saving ? (
              <ActivityIndicator size="small" color="#FFF" />
            ) : (
              <Text style={styles.saveButtonText}>Save Changes</Text>
            )}
          </TouchableOpacity>
        )}
      </ScrollView>

      {/* Dropdown Modal */}
      <Modal
        visible={modalVisible}
        transparent={true}
        animationType="fade"
        onRequestClose={() => setModalVisible(false)}
      >
        <TouchableOpacity
          style={styles.modalOverlay}
          activeOpacity={1}
          onPress={() => setModalVisible(false)}
        >
          <View style={styles.modalContent}>
            <View style={styles.modalHeader}>
              <Text style={styles.modalTitle}>
                {selectedCategory ? CATEGORY_LABELS[selectedCategory] : 'Select Delay'}
              </Text>
              <TouchableOpacity onPress={() => setModalVisible(false)}>
                <Text style={styles.modalClose}>Done</Text>
              </TouchableOpacity>
            </View>
            <FlatList
              data={selectedCategory && SENSITIVE_CATEGORIES.includes(selectedCategory)
                ? SENSITIVE_DELAY_OPTIONS
                : DELAY_OPTIONS}
              keyExtractor={(item) => item.value.toString()}
              renderItem={({ item }) => {
                const isSelected = selectedCategory && getCategoryValue(selectedCategory) === item.value;
                return (
                  <TouchableOpacity
                    style={[styles.modalOption, isSelected && styles.modalOptionSelected]}
                    onPress={() => selectOption(item.value)}
                  >
                    <Text style={[styles.modalOptionText, isSelected && styles.modalOptionTextSelected]}>
                      {item.label}
                    </Text>
                    {isSelected && (
                      <Text style={styles.modalCheckmark}>✓</Text>
                    )}
                  </TouchableOpacity>
                );
              }}
            />
          </View>
        </TouchableOpacity>
      </Modal>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#F3F6F8',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: ms(20),
    paddingVertical: ms(15),
    marginTop: ms(10),
    backgroundColor: '#F3F6F8',
  },
  headerTitle: {
    fontSize: ms(18, 0.3),
    fontWeight: 'bold',
    color: '#333',
  },
  headerIcon: {
    width: s(24),
    height: s(24),
    tintColor: '#333',
  },
  headerPlaceholder: {
    width: s(24),
    height: s(24),
  },
  loadingContainer: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
  loadingText: {
    marginTop: ms(12),
    fontSize: ms(16, 0.3),
    color: '#666',
  },
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    paddingHorizontal: ms(20),
    paddingBottom: ms(30),
    paddingTop: ms(50),
  },
  infoCard: {
    flexDirection: 'row',
    backgroundColor: '#E8F8F8',
    borderRadius: ms(12),
    padding: ms(16),
    marginBottom: ms(20),
    borderLeftWidth: ms(4),
    borderLeftColor: '#29BFC0',
  },
  infoCardIcon: {
    width: s(24),
    height: s(24),
    tintColor: '#29BFC0',
    marginRight: ms(12),
    marginTop: ms(2),
  },
  infoCardContent: {
    flex: 1,
  },
  infoCardTitle: {
    fontSize: ms(16, 0.3),
    fontWeight: '600',
    color: '#333',
    marginBottom: ms(4),
  },
  infoCardDescription: {
    fontSize: ms(14, 0.3),
    color: '#555',
    lineHeight: ms(20, 0.3),
  },
  section: {
    marginBottom: ms(24),
  },
  sectionHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: ms(12),
  },
  sectionIcon: {
    width: s(20),
    height: s(20),
    tintColor: '#29BFC0',
    marginRight: ms(8),
  },
  sectionTitle: {
    fontSize: ms(16, 0.3),
    fontWeight: '600',
    color: '#333',
  },
  sectionContent: {
    backgroundColor: '#FFF',
    borderRadius: ms(12),
    padding: ms(4),
    borderWidth: ms(1),
    borderColor: '#E0E0E0',
  },
  categoryRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: ms(8),
    paddingHorizontal: ms(12),
    borderBottomWidth: ms(1),
    borderBottomColor: '#F0F0F0',
  },
  categoryInfo: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    gap: ms(8),
  },
  categoryLabel: {
    fontSize: ms(14, 0.3),
    color: '#333',
  },
  sensitiveBadge: {
    backgroundColor: '#FFF3CD',
    paddingVertical: ms(2),
    paddingHorizontal: ms(6),
    borderRadius: ms(4),
  },
  sensitiveBadgeText: {
    fontSize: ms(10, 0.3),
    color: '#856404',
    fontWeight: '600',
  },
  pickerContainer: {
    width: s(130),
    backgroundColor: '#FFF',
    borderRadius: ms(8),
    overflow: 'hidden',
    borderWidth: ms(1),
    borderColor: '#29BFC0',
  },
  picker: {
    height: s(36),
    width: s(130),
  },
  pickerItem: {
    fontSize: ms(12, 0.3),
    height: s(100),
    color: '#333',
  },
  pickerItemText: {
    fontSize: ms(12, 0.3),
  },
  switchRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: ms(12),
    paddingHorizontal: ms(12),
    borderBottomWidth: ms(1),
    borderBottomColor: '#F0F0F0',
  },
  switchInfo: {
    flex: 1,
    marginRight: ms(12),
  },
  switchLabel: {
    fontSize: ms(14, 0.3),
    fontWeight: '500',
    color: '#333',
    marginBottom: ms(2),
  },
  switchDescription: {
    fontSize: ms(12, 0.3),
    color: '#666',
  },
  explanationCard: {
    backgroundColor: '#FFF',
    borderRadius: ms(12),
    padding: ms(16),
    marginBottom: ms(20),
    borderWidth: ms(1),
    borderColor: '#E0E0E0',
  },
  explanationTitle: {
    fontSize: ms(16, 0.3),
    fontWeight: '600',
    color: '#333',
    marginBottom: ms(12),
  },
  explanationItem: {
    flexDirection: 'row',
    marginBottom: ms(10),
  },
  bulletPoint: {
    fontSize: ms(14, 0.3),
    color: '#29BFC0',
    fontWeight: '600',
    marginRight: ms(8),
    width: s(20),
  },
  explanationText: {
    flex: 1,
    fontSize: ms(14, 0.3),
    color: '#555',
    lineHeight: ms(20, 0.3),
  },
  linkButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: '#FFF',
    borderRadius: ms(25),
    padding: ms(16),
    paddingHorizontal: ms(20),
    borderWidth: ms(1),
    borderColor: '#29BFC0',
  },
  linkButtonText: {
    fontSize: ms(16, 0.3),
    color: '#29BFC0',
    fontWeight: '600',
  },
  linkButtonIcon: {
    width: s(20),
    height: s(20),
    tintColor: '#29BFC0',
  },
  // Dropdown button styles
  dropdownButton: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#FFF',
    borderWidth: ms(1),
    borderColor: '#29BFC0',
    borderRadius: ms(8),
    paddingHorizontal: ms(12),
    paddingVertical: ms(8),
    minWidth: s(120),
    justifyContent: 'space-between',
  },
  dropdownButtonText: {
    fontSize: ms(13, 0.3),
    color: '#333',
    fontWeight: '500',
  },
  dropdownChevron: {
    width: s(14),
    height: s(14),
    tintColor: '#29BFC0',
    marginLeft: ms(6),
    transform: [{ rotate: '90deg' }],
  },
  // Modal styles
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  modalContent: {
    backgroundColor: '#FFF',
    borderRadius: ms(16),
    width: '80%',
    maxWidth: s(320),
    maxHeight: '60%',
    overflow: 'hidden',
  },
  modalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: ms(16),
    borderBottomWidth: ms(1),
    borderBottomColor: '#E0E0E0',
    backgroundColor: '#F8F8F8',
  },
  modalTitle: {
    fontSize: ms(16, 0.3),
    fontWeight: '600',
    color: '#333',
  },
  modalClose: {
    fontSize: ms(16, 0.3),
    fontWeight: '600',
    color: '#29BFC0',
  },
  modalOption: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: ms(14),
    paddingHorizontal: ms(16),
    borderBottomWidth: ms(1),
    borderBottomColor: '#F0F0F0',
  },
  modalOptionSelected: {
    backgroundColor: '#E8F8F8',
  },
  modalOptionText: {
    fontSize: ms(15, 0.3),
    color: '#333',
  },
  modalOptionTextSelected: {
    color: '#29BFC0',
    fontWeight: '600',
  },
  modalCheckmark: {
    fontSize: ms(18, 0.3),
    color: '#29BFC0',
    fontWeight: 'bold',
  },
  // Save button styles
  saveButton: {
    backgroundColor: '#29BFC0',
    borderRadius: ms(25),
    padding: ms(16),
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: ms(20),
    marginBottom: ms(10),
  },
  saveButtonDisabled: {
    backgroundColor: '#A0D8D8',
  },
  saveButtonText: {
    color: '#FFF',
    fontSize: ms(16, 0.3),
    fontWeight: '600',
  },
});

export default DeathAccessPolicyScreen;
